import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Plus, ArrowRightLeft, Trash2, Sparkles } from "lucide-react";

const CATEGORIES = ["energy","mass","volume","ghg","currency","percentage","count","time","unknown"];

export default function UnitsConversion() {
  const [data, setData] = useState(null);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ symbol:"", name:"", category:"energy", base_symbol:"", factor_to_base:1, source:"manual" });
  const [convForm, setConvForm] = useState({ value: 100, from: "kWh", to: "MWh" });
  const [convResult, setConvResult] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => { const r = await api.get("/admin/units"); setData(r.data); };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/units", form);
      setShow(false);
      setForm({ symbol:"", name:"", category:"energy", base_symbol:"", factor_to_base:1, source:"manual" });
      await load();
    } catch (err) { alert(err?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this unit?")) return;
    await api.delete(`/admin/units/${id}`);
    await load();
  };

  const convert = async () => {
    try {
      const r = await api.get("/admin/units/convert", { params: {
        value: Number(convForm.value), from_symbol: convForm.from, to_symbol: convForm.to,
      }});
      setConvResult(r.data);
    } catch (e) { setConvResult({ error: e?.response?.data?.detail || "Failed" }); }
  };

  if (!data) return <div className="p-6 text-ink-soft">Loading…</div>;
  const filtered = data.units.filter(u => !filter || u.category === filter);

  return (
    <div className="p-6 space-y-5" data-testid="units-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="section-label">Admin · Units</div>
          <h1 className="text-2xl font-bold tracking-tight mt-1">ESG Units & Conversions</h1>
          <p className="text-sm text-ink-soft">Master unit catalogue with base conversions. New units appearing in imported activity data are auto-added with <span className="pill">auto_imported</span> tag for review.</p>
        </div>
        <button data-testid="unit-new-button" onClick={()=>setShow(true)} className="btn btn-primary"><Plus size={14}/> New unit</button>
      </div>

      <div className="card p-4">
        <div className="section-label flex items-center gap-2"><ArrowRightLeft size={12}/> Quick converter</div>
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <input data-testid="conv-value" type="number" value={convForm.value} onChange={e=>setConvForm({...convForm, value:e.target.value})} className="border border-border rounded-md px-3 py-2 text-sm w-28"/>
          <select data-testid="conv-from" value={convForm.from} onChange={e=>setConvForm({...convForm, from:e.target.value})} className="border border-border rounded-md px-3 py-2 text-sm">
            {data.units.map(u => <option key={u.id} value={u.symbol}>{u.symbol}</option>)}
          </select>
          <span className="text-ink-soft text-sm">→</span>
          <select data-testid="conv-to" value={convForm.to} onChange={e=>setConvForm({...convForm, to:e.target.value})} className="border border-border rounded-md px-3 py-2 text-sm">
            {data.units.map(u => <option key={u.id} value={u.symbol}>{u.symbol}</option>)}
          </select>
          <button data-testid="conv-button" onClick={convert} className="btn btn-secondary"><Sparkles size={12}/> Convert</button>
          {convResult && (
            convResult.error
              ? <span className="text-status-error text-sm">{convResult.error}</span>
              : <span className="text-sm font-mono" data-testid="conv-result">
                  = {convResult.result} <span className="text-ink-soft">{convResult.to}</span>
                </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button onClick={()=>setFilter("")} className={"pill " + (!filter ? "pill-success" : "")} data-testid="cat-all">all</button>
        {CATEGORIES.map(c => (
          <button key={c} onClick={()=>setFilter(c)} data-testid={`cat-${c}`} className={"pill " + (filter === c ? "pill-success" : "")}>{c}</button>
        ))}
      </div>

      <div className="card overflow-hidden">
        <table className="dense">
          <thead>
            <tr>
              <th>Symbol</th><th>Name</th><th>Category</th>
              <th>Base</th><th className="text-right">Factor → base</th>
              <th>Source</th><th>Updated</th><th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(u => (
              <tr key={u.id} data-testid={`unit-${u.symbol}`}>
                <td className="font-mono font-semibold">{u.symbol}</td>
                <td>{u.name}</td>
                <td><span className="pill">{u.category}</span></td>
                <td className="font-mono">{u.base_symbol}</td>
                <td className="num">{u.factor_to_base}</td>
                <td>
                  {u.auto_imported
                    ? <span className="pill pill-warn">auto-imported</span>
                    : <span className="pill">{u.source}</span>}
                </td>
                <td className="text-xs font-mono text-ink-soft">{u.updated_at?.slice(0,10)}</td>
                <td className="text-right">
                  <button data-testid={`unit-del-${u.symbol}`} onClick={()=>remove(u.id)} className="btn btn-ghost text-status-error"><Trash2 size={12}/></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={()=>setShow(false)}>
          <div className="card w-full max-w-md p-5 bg-white" onClick={e=>e.stopPropagation()}>
            <div className="section-label">New unit</div>
            <form onSubmit={create} className="mt-3 space-y-3 text-sm">
              {[
                ["symbol", "Symbol (e.g. kWh)"],
                ["name", "Name"],
                ["base_symbol", "Base symbol (target of conversion)"],
                ["source", "Source"],
              ].map(([k, l]) => (
                <div key={k}>
                  <label className="dlabel">{l}</label>
                  <input data-testid={`unit-form-${k}`} required={["symbol","name","base_symbol"].includes(k)} value={form[k]||""} onChange={e=>setForm({...form, [k]:e.target.value})} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
                </div>
              ))}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="dlabel">Category</label>
                  <select data-testid="unit-form-category" value={form.category} onChange={e=>setForm({...form, category:e.target.value})} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm">
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="dlabel">Factor → base</label>
                  <input data-testid="unit-form-factor" required type="number" step="any" value={form.factor_to_base} onChange={e=>setForm({...form, factor_to_base:Number(e.target.value)})} className="mt-1 w-full border border-border rounded-md px-3 py-2 text-sm"/>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={()=>setShow(false)} className="btn btn-ghost">Cancel</button>
                <button data-testid="unit-form-submit" className="btn btn-primary">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
